import { useCallback, useEffect, useState } from 'react';
import { LuPencil, LuRefreshCw, LuUnplug } from 'react-icons/lu';
import aviso from '../../lib/aviso';
import RecordPanel, { PanelSection, Switch } from '../RecordPanel';
import Select from '../Select';
import Dialog from '../Dialog';
import CampoPersonalizadoService, { type CampoPersonalizado } from '../../services/CampoPersonalizadoService';
import {
    RdStationService, type RdStationCampo, type RdStationCampoMapeado, type RdStationConexao,
    type RdStationEntrada, type RdStationSaida,
} from '../../services/RdStationService';
import { formatarData } from '../conexoes/canais';

/**
 * O painel da conexão RD em Conexões — as três abas do desenho fechado em 01/10/2026
 * (docs/propostas/rd-station.html §5): Entrada, Saída e Campos. Conectar não mora aqui:
 * é a porta da tela Apps. Só o administrador edita; os outros veem.
 */

interface Props {
    open: boolean;
    onClose: () => void;
    admin: boolean;
    usuarios: { id: number; name: string }[];
    /** A conexão foi removida ou mudou: a lista de Conexões recarrega. */
    onMudou: () => void;
}

type Aba = 'entrada' | 'saida' | 'campos';

/** Os interruptores da aba Saída (o campo da etapa é escolha, não interruptor). */
type SaidaLigavel = 'conversao' | 'oportunidade' | 'venda' | 'perdida';

interface Rascunho { entrada: RdStationEntrada; saida: RdStationSaida; campos: RdStationCampoMapeado[] }

const NAO_TRAZER = '__nao__';
const DISTRIBUIR = '__distribuir__';

/** Os campos padrão da RD têm lugar certo no contato: não se escolhem. */
const PADRAO: { rd: string; crm: string }[] = [
    { rd: 'email', crm: 'E-mail' },
    { rd: 'name', crm: 'Nome' },
    { rd: 'mobile_phone', crm: 'Telefone' },
    { rd: 'city / state', crm: 'Cidade / Estado' },
];

const relativo = (iso: string | null): string => {
    if (!iso) return 'nenhum ainda';
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
    if (Number.isNaN(min)) return 'nenhum ainda';
    if (min < 1) return 'agora mesmo';
    if (min < 60) return `há ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `há ${h}h`;
    const d = Math.floor(h / 24);
    return d === 1 ? 'há 1 dia' : `há ${d} dias`;
};

const RdStationPanel = ({ open, onClose, admin, usuarios, onMudou }: Props) => {
    const [aba, setAba] = useState<Aba>('entrada');
    const [conexao, setConexao] = useState<RdStationConexao | null>(null);
    const [r, setR] = useState<Rascunho | null>(null);
    const [camposRd, setCamposRd] = useState<RdStationCampo[] | null>(null);
    const [erroCampos, setErroCampos] = useState('');
    const [camposCrm, setCamposCrm] = useState<CampoPersonalizado[]>([]);
    const [ocupado, setOcupado] = useState(false);
    const [confirmar, setConfirmar] = useState(false);
    /* Ver e editar no mesmo painel. Com abas, o Editar mora dentro da aba (padrão do kit);
       editando, as abas continuam e o rodapé traz Cancelar e Salvar para as três de uma vez. */
    const [editando, setEditando] = useState(false);

    const aplicar = (c: RdStationConexao) => {
        setConexao(c);
        setR({ entrada: c.entrada, saida: c.saida, campos: c.campos });
    };

    const carregar = useCallback(async () => {
        const res = await RdStationService.obter();
        if (res.sucesso && res.dados?.conectada) aplicar(res.dados);
        else { aviso.erro(res.mensagem || 'Não foi possível ler a conexão com o RD Station.'); onClose(); }
    }, [onClose]);

    useEffect(() => {
        if (!open) return;
        setAba('entrada');
        setEditando(false);
        setConexao(null);
        setCamposRd(null);
        setErroCampos('');
        carregar();
    }, [open, carregar]);

    /* Os campos da RD só são lidos quando uma aba que os usa abre (Saída escolhe o campo da
       etapa, Campos pareia): é uma ida à RD, e a lista é por conta. */
    useEffect(() => {
        if (!open || aba === 'entrada' || camposRd !== null) return;
        (async () => {
            const [rd, crm] = await Promise.all([
                RdStationService.campos(),
                CampoPersonalizadoService.buscarTodos().catch(() => [] as CampoPersonalizado[]),
            ]);
            setCamposCrm(crm);
            if (rd.sucesso && rd.dados) setCamposRd(rd.dados);
            else { setCamposRd([]); setErroCampos(rd.mensagem || 'Não foi possível ler os campos da conta RD.'); }
        })();
    }, [open, aba, camposRd]);

    const salvar = async () => {
        if (!r) return;
        if (r.entrada.responsavel === 'usuario' && !r.entrada.usuario_id) {
            aviso.atencao('Escolha quem fica responsável pelas oportunidades do RD.');
            setAba('entrada');
            return;
        }
        setOcupado(true);
        const res = await RdStationService.salvar(r);
        setOcupado(false);
        if (!res.sucesso || !res.dados) { aviso.erro(res.mensagem || 'Não foi possível salvar.'); return; }
        aplicar(res.dados);
        setEditando(false);
        aviso.ok('Configuração do RD Station salva.');
    };

    const reativar = async () => {
        setOcupado(true);
        const res = await RdStationService.reativarEntrada();
        setOcupado(false);
        if (res.sucesso && res.dados) { aplicar(res.dados); aviso.ok(res.mensagem || 'As conversões voltaram a entrar.'); onMudou(); }
        else aviso.erro(res.mensagem || 'A RD ainda recusa o envio das conversões.');
    };

    const desconectar = async () => {
        setOcupado(true);
        const res = await RdStationService.desconectar();
        setOcupado(false);
        setConfirmar(false);
        if (!res.sucesso) { aviso.erro(res.mensagem || 'Não foi possível desconectar.'); return; }
        aviso.ok('RD Station desconectado.');
        onMudou();
        onClose();
    };

    const mapaDe = (rd: string) => r?.campos.find(c => c.rd === rd)?.campo_personalizado_id ?? null;
    const parear = (rd: string, id: number | null) => setR(x => x && ({
        ...x,
        campos: [...x.campos.filter(c => c.rd !== rd), { rd, campo_personalizado_id: id }],
    }));

    const travado = !admin || ocupado || !editando;
    const nomeDoUsuario = (id: number | null) => usuarios.find(u => u.id === id)?.name ?? `Usuário ${id}`;
    const nomeDoCampo = (id: number | null) => camposCrm.find(f => f.campoPersonalizadoId === id)?.nome ?? 'Campo removido';
    /* A etapa é texto: só campo personalizado de texto da RD recebe. */
    const camposTexto = (camposRd ?? []).filter(c => c.personalizado && c.tipoDado === 'STRING');
    const nomeDoCampoRd = (id: string) => camposRd?.find(c => c.identificador === id)?.nome ?? id;
    const botaoEditar = admin && !editando && (
        <button type="button" className="rp-btn" onClick={() => setEditando(true)}>
            <LuPencil size={13} /> Editar
        </button>
    );
    const expirada = conexao?.status === 3;

    return (
        <>
            <RecordPanel
                open={open}
                onClose={onClose}
                initials="RD"
                squareAvatar
                title="RD Station Marketing"
                subtitle={conexao
                    ? `${conexao.conta || 'Conta conectada'} · conectada em ${formatarData(conexao.conectadaEm)}`
                    : 'Carregando…'}
                tabs={[
                    { id: 'entrada', label: 'Entrada' },
                    { id: 'saida', label: 'Saída' },
                    { id: 'campos', label: 'Campos' },
                ]}
                activeTab={aba}
                onTabChange={id => setAba(id as Aba)}
                editing={editando}
                onBack={() => { if (conexao) aplicar(conexao); setEditando(false); }}
                footer={
                    <>
                        <button type="button" className="rp-btn" onClick={() => { if (conexao) aplicar(conexao); setEditando(false); }} disabled={ocupado}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={ocupado}>
                            {ocupado ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                }
            >
                {!conexao || !r ? (
                    <div className="lk-skeleton" style={{ height: 120 }} />
                ) : aba === 'entrada' ? (
                    <>
                        <PanelSection title="Conta conectada" extra={!editando && admin ? (
                            <button type="button" className="rp-btn is-danger" onClick={() => setConfirmar(true)} disabled={ocupado}>
                                <LuUnplug size={13} /> Desconectar
                            </button>
                        ) : undefined}>
                            <div className="rp-kv cx-kv">
                                <span className="rp-k">Conta</span><span className="rp-v">{conexao.conta || '—'}</span>
                                <span className="rp-k">Último evento</span><span className="rp-v">{relativo(conexao.ultimoEvento)}</span>
                                <span className="rp-k">Recebidos hoje</span>
                                <span className="rp-v">
                                    {conexao.recebidosHoje} {conexao.recebidosHoje === 1 ? 'conversão' : 'conversões'} · {conexao.oportunidadesHoje} {conexao.oportunidadesHoje === 1 ? 'oportunidade' : 'oportunidades'}
                                </span>
                                <span className="rp-k">Entrada</span>
                                <span className={`rp-v ${conexao.webhookAtivo ? '' : 'cx-warn'}`}>
                                    {expirada ? 'Acesso expirado — conecte de novo pela tela Apps'
                                        : conexao.webhookAtivo ? 'Recebendo as conversões'
                                        : `Parada: ${conexao.webhookErro || 'a RD não aceitou o envio'}`}
                                </span>
                            </div>
                            {!conexao.webhookAtivo && !expirada && admin && (
                                <button type="button" className="rp-btn" onClick={reativar} disabled={ocupado} style={{ marginTop: 10 }}>
                                    <LuRefreshCw size={13} /> Tentar de novo
                                </button>
                            )}
                        </PanelSection>

                        <PanelSection title="O que fazer com quem converte" extra={botaoEditar || undefined}>
                            {!editando ? (
                                <div className="rp-kv cx-kv">
                                    <span className="rp-k">Quem converte</span>
                                    <span className="rp-v">{r.entrada.acao === 'oportunidade' ? 'Criar contato e abrir oportunidade' : 'Só criar o contato'}</span>
                                    {r.entrada.acao === 'oportunidade' && (
                                        <>
                                            <span className="rp-k">Etapa de entrada</span><span className="rp-v">Não iniciado</span>
                                            <span className="rp-k">Responsável</span>
                                            <span className="rp-v">{r.entrada.responsavel === 'usuario' && r.entrada.usuario_id ? nomeDoUsuario(r.entrada.usuario_id) : 'Distribuir entre quem recebe lead'}</span>
                                        </>
                                    )}
                                </div>
                            ) : (<>
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="rd-acao">Ao receber uma conversão</label>
                                <Select id="rd-acao" disabled={travado} value={r.entrada.acao}
                                    options={[
                                        { value: 'oportunidade', label: 'Criar contato e abrir oportunidade' },
                                        { value: 'contato', label: 'Só criar o contato' },
                                    ]}
                                    onChange={v => setR(x => x && ({ ...x, entrada: { ...x.entrada, acao: v as RdStationEntrada['acao'] } }))} />
                            </div>
                            {r.entrada.acao === 'oportunidade' && (
                                <>
                                    <div className="rp-field">
                                        <span className="rp-label">Etapa de entrada</span>
                                        <span className="rp-v">Não iniciado</span>
                                    </div>
                                    <div className="rp-field">
                                        <label className="rp-label" htmlFor="rd-resp">Responsável</label>
                                        <Select id="rd-resp" disabled={travado}
                                            value={r.entrada.responsavel === 'usuario' && r.entrada.usuario_id ? String(r.entrada.usuario_id) : DISTRIBUIR}
                                            options={[
                                                { value: DISTRIBUIR, label: 'Distribuir entre quem recebe lead' },
                                                ...usuarios.map(u => ({ value: String(u.id), label: u.name })),
                                            ]}
                                            onChange={v => setR(x => x && ({
                                                ...x,
                                                entrada: v === DISTRIBUIR
                                                    ? { ...x.entrada, responsavel: 'distribuir', usuario_id: null }
                                                    : { ...x.entrada, responsavel: 'usuario', usuario_id: Number(v) },
                                            }))} />
                                        <p className="rp-hint">Distribuir usa a mesma régua das conversas novas: quem recebe lead e tem menos conversas abertas.</p>
                                    </div>
                                </>
                            )}
                            </>)}
                            <p className="rp-hint">
                                Contato sem telefone é casado pelo e-mail e entra assim mesmo. Ele aparece na Pipeline, mas não no Chat — não há número para responder.
                            </p>
                        </PanelSection>
                    </>
                ) : aba === 'saida' ? (
                    <>
                        <PanelSection title="O que vai para o RD" extra={botaoEditar || undefined}>
                            {([
                                ['conversao', 'Leads que nascem aqui', 'WhatsApp, Instagram, cadastro manual… viram conversão "Brik CRM" na RD, com telefone e, se houver, e-mail. Campanhas ficam de fora.'],
                                ['oportunidade', 'Lead qualificado', 'Quando sai de Em qualificação ou do atendimento da IA para Em negociação ou Proposta enviada: vira Lead Qualificado e oportunidade na RD.'],
                                ['venda', 'Venda ganha', 'Com o valor da oportunidade.'],
                                ['perdida', 'Oportunidade perdida', 'Com o motivo da perda.'],
                            ] as [SaidaLigavel, string, string][]).map(([chave, rotulo, dica]) => (
                                <div key={chave} className="rp-toggle-row">
                                    <div className="rp-toggle-text">
                                        <span className="rp-toggle-label">{rotulo}</span>
                                        <span className="rp-toggle-hint">{dica}</span>
                                    </div>
                                    <Switch checked={r.saida[chave]} label={rotulo} disabled={travado}
                                        onChange={v => setR(x => x && ({ ...x, saida: { ...x.saida, [chave]: v } }))} />
                                </div>
                            ))}
                            <p className="rp-hint">
                                Vale para todas as oportunidades da empresa, venham de onde vierem. O que veio do RD não volta como conversão: já existe lá.
                            </p>
                        </PanelSection>

                        <PanelSection title="Etapa no RD">
                            {!editando ? (
                                <div className="rp-kv cx-kv">
                                    <span className="rp-k">Campo</span>
                                    <span className="rp-v">{r.saida.campo_etapa ? nomeDoCampoRd(r.saida.campo_etapa) : 'Não enviar'}</span>
                                </div>
                            ) : camposRd === null ? (
                                <div className="lk-skeleton" style={{ height: 36 }} />
                            ) : (
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="rd-campo-etapa">Campo que recebe a etapa</label>
                                    <Select id="rd-campo-etapa" disabled={travado}
                                        value={r.saida.campo_etapa ?? NAO_TRAZER}
                                        options={[
                                            { value: NAO_TRAZER, label: 'Não enviar' },
                                            ...camposTexto.map(c => ({ value: c.identificador, label: `${c.nome} (${c.identificador})` })),
                                        ]}
                                        onChange={v => setR(x => x && ({ ...x, saida: { ...x.saida, campo_etapa: v === NAO_TRAZER ? null : v } }))} />
                                </div>
                            )}
                            <p className="rp-hint">
                                A cada mudança de etapa aqui, o nome dela ("Em qualificação", "Em negociação"…) é gravado nesse campo
                                do contato na RD — dá para segmentar e disparar automação por ele. Crie na RD um campo de texto
                                (por exemplo "Etapa no Brik") e escolha aqui.
                                {erroCampos && ` ${erroCampos}`}
                            </p>
                        </PanelSection>
                    </>
                ) : (
                    <>
                        <PanelSection title="Padrão — sempre pareado">
                            <div className="rp-kv cx-kv">
                                {PADRAO.map(p => (
                                    <span key={p.rd} style={{ display: 'contents' }}>
                                        <span className="rp-k cx-mono">{p.rd}</span><span className="rp-v">{p.crm}</span>
                                    </span>
                                ))}
                            </div>
                        </PanelSection>

                        <PanelSection title="Da conta — escolhidos por você" extra={botaoEditar || undefined}>
                            {camposRd === null ? (
                                <div className="lk-skeleton" style={{ height: 80 }} />
                            ) : erroCampos ? (
                                <p className="rp-hint cx-warn">{erroCampos}</p>
                            ) : camposRd.filter(c => c.personalizado).length === 0 ? (
                                <p className="rp-hint">Esta conta RD não tem campos personalizados.</p>
                            ) : (
                                camposRd.filter(c => c.personalizado).map(c => {
                                    const atual = mapaDe(c.identificador);
                                    if (!editando) return (
                                        <div key={c.identificador} className="rp-kv cx-kv">
                                            <span className="rp-k">{c.nome} <span className="lk-muted cx-mono">· {c.identificador}</span></span>
                                            <span className="rp-v">{atual === null ? <span className="lk-empty-value">não trazer</span> : nomeDoCampo(atual)}</span>
                                        </div>
                                    );
                                    return (
                                        <div key={c.identificador} className="rp-field">
                                            <label className="rp-label" htmlFor={`rd-cf-${c.identificador}`}>
                                                {c.nome} <span className="lk-muted cx-mono">· {c.identificador}</span>
                                            </label>
                                            <Select id={`rd-cf-${c.identificador}`} disabled={travado}
                                                value={atual === null ? NAO_TRAZER : String(atual)}
                                                options={[
                                                    { value: NAO_TRAZER, label: 'Não trazer' },
                                                    /* Um campo do CRM recebe de um campo da RD só: dois escrevendo no
                                                       mesmo lugar, o segundo apagaria o primeiro a cada conversão. */
                                                    ...camposCrm.map(f => {
                                                        const dono = r.campos.find(m => m.campo_personalizado_id === f.campoPersonalizadoId && m.rd !== c.identificador);
                                                        return { value: String(f.campoPersonalizadoId), label: f.nome, disabled: !!dono, meta: dono ? `já recebe ${dono.rd}` : undefined };
                                                    }),
                                                ]}
                                                onChange={v => parear(c.identificador, v === NAO_TRAZER ? null : Number(v))} />
                                        </div>
                                    );
                                })
                            )}
                            <p className="rp-hint">
                                O valor vai para o campo personalizado da oportunidade. O que não for pareado fica guardado no lead e não se perde.
                                {camposCrm.length === 0 && camposRd !== null && !erroCampos && ' A empresa ainda não tem campos personalizados — crie em Configurações › Campos personalizados.'}
                            </p>
                        </PanelSection>
                    </>
                )}
            </RecordPanel>

            <Dialog
                open={confirmar}
                onClose={() => setConfirmar(false)}
                title="Desconectar o RD Station?"
                confirmLabel="Desconectar"
                danger
                busy={ocupado}
                onConfirm={desconectar}
            >
                As conversões deixam de entrar e os marcos do funil deixam de voltar para o RD. Contatos e oportunidades que já entraram continuam no CRM.
            </Dialog>
        </>
    );
};

export default RdStationPanel;
